import React, { useState, useEffect, useRef } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { FluxButton } from '@meltwater/flux-web-components-react';
import { Stack, Snackbar, Alert, Tooltip, IconButton, Box, Typography, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TableGuide from '../TableGuide';
import styles from './DataTable.module.css';

/**
 * DataTable - A reusable data table component with loading, error, and back navigation
 * 
 * @param {Object} props Component props
 * @param {Array} props.columns Array of column definitions for MaterialReactTable
 * @param {Array} props.data Array of data objects to display
 * @param {boolean} props.isLoading Whether the data is currently loading
 * @param {string} props.loadingMessage Message to display during loading
 * @param {boolean} props.hasError Whether there's an error to display
 * @param {string} props.errorMessage Error message to display when hasError is true
 * @param {Function} props.onBackClick Function to call when back/return button is clicked
 * @param {Function} props.renderToolbarActions Function to render custom toolbar actions
 * @param {Object} props.snackbarProps Props for the snackbar notification
 * @param {Object} props.tableProps Additional props to pass to MaterialReactTable
 * @param {string} props.errorTitle Title for error message display
 * @param {string} props.backTooltip Tooltip for the back button
 * @returns {React.ReactElement} DataTable component
 */
const DataTable = ({
  columns,
  data = [],
  isLoading = false,
  loadingMessage = 'Loading data...',
  hasError = false,
  errorMessage = 'An error occurred',
  onBackClick,
  renderToolbarActions,
  snackbarProps = { open: false },
  tableProps = {},
  errorTitle = 'Error Loading Data',
  backTooltip = 'Return to Dashboard',
  showGuide = false,
  guideSteps = null
}) => {
  const [showInteractiveGuide, setShowInteractiveGuide] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const tableContainerRef = useRef(null);
  const defaultColumnCount = columns.length;
  
  // Pre-compute all memoized values at the top level
  // to avoid conditional hook calls
  const memoizedTableBodyCellProps = React.useMemo(() => {
    return isLoading ? {
      sx: {
        // Apply disabled styling to any buttons within cells
        '& button, & [role="button"], & a, & .MuiIconButton-root': {
          opacity: 0.6,
          pointerEvents: 'none',
          position: 'relative',
          cursor: 'not-allowed',
          '&::after': {
            content: '"🚫"',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px',
            zIndex: 2
          }
        }
      }
    } : {};
  }, [isLoading]);
  
  const memoizedTableHeadCellProps = React.useMemo(() => {
    return isLoading ? {
      sx: {
        // Disable column sorting and menu buttons while loading
        '& button, & .MuiTableSortLabel-root': {
          opacity: 0.6,
          pointerEvents: 'none',
          cursor: 'not-allowed'
        }
      }
    } : {};
  }, [isLoading]);
  
  const memoizedTablePaperProps = React.useMemo(() => {
    return isLoading ? {
      sx: {
        // Disable all interactive elements except back button
        '& button:not(.backButton), & [role="button"]:not(.backButton), & .MuiIconButton-root:not(.backButton)': {
          opacity: 0.6,
          pointerEvents: 'none',
          cursor: 'not-allowed'
        },
        // Disable pagination controls
        '& .MuiTablePagination-root button': {
          opacity: 0.6,
          pointerEvents: 'none',
          cursor: 'not-allowed'
        },
        // Disable column visibility selector
        '& .MuiMenu-root, & .MuiPopover-root': {
          opacity: 0.6,
          pointerEvents: 'none'
        }
      }
    } : {};
  }, [isLoading]);
  
  const memoizedTableState = React.useMemo(() => {
    return {
      isLoading: isLoading,
      showSkeletons: isLoading,
      ...(tableProps.state || {})
    };
  }, [isLoading, tableProps.state]);
  
  const memoizedLinearProgressProps = React.useMemo(() => {
    return {
      sx: { 
        height: '4px',
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#188182',
        }
      }
    };
  }, []);

  // Check if columns are expanded beyond the default view
  useEffect(() => {
    if (!isLoading && !hasError && tableContainerRef.current) {
      // Use requestAnimationFrame to defer DOM reads until the next paint
      requestAnimationFrame(() => {
        const visibleColumnsCount = tableContainerRef.current.querySelectorAll('.mrt-header-cell').length;
        setIsExpanded(visibleColumnsCount > defaultColumnCount);
      });
    }
  }, [data, isLoading, hasError, defaultColumnCount]);

  // Load guide after a short delay when table is first rendered
  useEffect(() => {
    if (showGuide && !isLoading && !hasError && data.length > 0) {
      // Reduced delay and use requestIdleCallback if available for better performance
      const showGuideWhenIdle = () => {
        setShowInteractiveGuide(true);
      };
      
      const timerId = window.requestIdleCallback 
        ? window.requestIdleCallback(showGuideWhenIdle, { timeout: 1000 })
        : setTimeout(showGuideWhenIdle, 1000);
        
      return () => {
        if (window.requestIdleCallback) {
          window.cancelIdleCallback(timerId);
        } else {
          clearTimeout(timerId);
        }
      };
    }
  }, [isLoading, hasError, data.length, showGuide]);
  
  // Component to display during error state
  const ErrorComponent = () => (
    <Paper 
      elevation={3}
      className={styles.errorContainer}
      onClick={(e) => {
        // Prevent any click events from propagating or triggering refreshes
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
    >
      <Box sx={{ mb: 3 }}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />
      </Box>
      <Typography variant="h5" sx={{ mb: 2, color: '#d32f2f', fontWeight: 'bold' }}>
        {errorTitle}
      </Typography>
      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
        We couldn't load the requested data from the server.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
        Error: {errorMessage}
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: '#444', fontStyle: 'italic' }}>
        To reload the data, please return to the dashboard and select this option again.
      </Typography>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        {onBackClick && (
          <FluxButton 
            tint="primary"
            type="filled"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBackClick();
            }}
          >
            Return to Dashboard
          </FluxButton>
        )}
      </Box>
    </Paper>
  );

  // Enhanced toolbar that preserves back button functionality during loading
  const renderToolbarWithLoadingState = ({ table }) => {
    // Check if tableProps has a custom renderTopToolbarCustomActions
    const hasCustomToolbar = tableProps && tableProps.renderTopToolbarCustomActions;
    
    if (hasCustomToolbar) {
      // Use the custom toolbar from tableProps but add the active back button styling
      const customToolbarContent = tableProps.renderTopToolbarCustomActions({ table });
      
      // If loading, just disable actions but keep visual appearance
      if (isLoading && customToolbarContent) {
        return React.cloneElement(
          customToolbarContent,
          {},
          React.Children.map(customToolbarContent.props.children, child => {
            // If this is the back button (checking for ArrowBackIcon children)
            if (child && child.props && child.props.children && 
                React.isValidElement(child.props.children) && 
                child.props.children.type === ArrowBackIcon) {
              return React.cloneElement(child, { 
                className: `${child.props.className || ''} ${styles.backButtonActive}`
              });
            }
            // Disable action buttons with prohibited symbol
            else if (child && React.isValidElement(child) && (child.type === FluxButton || child.type === IconButton)) {
              return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {React.cloneElement(child, { 
                    disabled: true,
                    className: `${child.props.className || ''} ${styles.disabledButton}`
                  })}
                  <div className={styles.buttonDisabledOverlay}></div>
                </div>
              );
            }
            return child;
          })
        );
      }
      
      return customToolbarContent;
    }
    
    // Default toolbar if no custom toolbar is provided
    const toolbarContent = (
      <Stack direction="row" spacing={1} alignItems="center" className={styles.toolbarContainer}>
        <Tooltip title={backTooltip}>
          <IconButton 
            onClick={onBackClick} 
            className={`${styles.backButton} ${isLoading ? styles.backButtonActive : ''}`}
          >
            <ArrowBackIcon className={styles.backIcon} />
          </IconButton>
        </Tooltip>
        
        {renderToolbarActions && (
          isLoading ? (
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              {React.cloneElement(renderToolbarActions({ table }), {
                className: styles.disabledButton
              })}
              <div className={styles.buttonsDisabledArea}></div>
            </div>
          ) : renderToolbarActions({ table })
        )}
        
        <Box sx={{ flexGrow: 1 }} />
        
        {isLoading ? (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Tooltip title="Guide...">
              <span>
                <IconButton 
                  sx={{ color: '#188182' }}
                  disabled={true}
                  className={styles.disabledButton}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
            <div className={styles.buttonDisabledOverlay}></div>
          </div>
        ) : (
          <Tooltip title="Guide...">
            <IconButton 
              onClick={() => setShowInteractiveGuide(true)}
              sx={{ color: '#188182' }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );

    return toolbarContent;
  };

  return (
    <div 
      className={styles.container}
      onClick={(e) => {
        if (hasError) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {hasError ? (
        <ErrorComponent />
      ) : (
        <div 
          className={`${styles.dataTableContainer} ${styles.tableLoadingContainer} ${isExpanded ? styles.expandedTable : ''}`}
          ref={tableContainerRef}
        >
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingMessageContainer}>
                <div className={styles.loadingMessage}>
                  {loadingMessage}
                  {!loadingMessage.includes('%') && <span className={styles.loadingDots}></span>}
                </div>
                {onBackClick && (
                  <Box className={styles.backButtonContainer}>
                    <FluxButton 
                      onClick={onBackClick} 
                      startIcon={<ArrowBackIcon fontSize="small" />}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      className={styles.overlayBackButton}
                    >
                      Back to Dashboard
                    </FluxButton>
                  </Box>
                )}
              </div>
            </div>
          )}
          
          <div>
            <MaterialReactTable
              columns={columns}
              data={isLoading ? [] : data}
              enableRowSelection={true}
              enableColumnOrdering
              enableGlobalFilter={true}
              enableColumnFilters={false}
              enableStickyHeader
              enableStickyFooter
              muiSearchTextFieldProps={{
                placeholder: 'Search...',
                variant: 'standard',
                sx: { width: '300px' },
                disabled: isLoading
              }}
              muiBottomToolbarProps={{
                sx: {
                  '& .MuiLinearProgress-root': {
                    display: 'none', // Hide progress bar in the footer
                  }
                }
              }}
              // Use pre-computed memoized props
              muiTableBodyCellProps={memoizedTableBodyCellProps}
              muiTableHeadCellProps={memoizedTableHeadCellProps}
              muiTablePaperProps={memoizedTablePaperProps}
              initialState={{ 
                density: 'compact',
                pagination: { pageSize: 50 }
              }}
              enableGlobalFilterModes
              renderTopToolbarCustomActions={renderToolbarWithLoadingState}
              positionToolbarAlertBanner="bottom"
              state={memoizedTableState}
              muiLinearProgressProps={memoizedLinearProgressProps}
              {...tableProps}
            />
          </div>
          
          {showInteractiveGuide && !isLoading && (
            <TableGuide
              onClose={() => setShowInteractiveGuide(false)}
              tableRef={tableContainerRef}
              customSteps={guideSteps}
            />
          )}
        </div>
      )}
      <Snackbar 
        open={snackbarProps.open} 
        onClose={snackbarProps.onClose}
        autoHideDuration={snackbarProps.autoHideDuration || 10000}
      >
        <Alert 
          severity={snackbarProps.severity || 'info'}
          onClose={snackbarProps.onClose}
          sx={{ width: '100%' }}
        >
          {snackbarProps.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DataTable;